import { useState } from "react";
import type { FormEvent } from "react";
import { Alert, Button, Card, Field, Input, Select } from "@empite/ui-components";
import { useReport, useReportReasons } from "../marketplace-hooks";
import type { ReportInput } from "../marketplace-hooks";
import { ErrorAlert } from "./common";

/**
 * The reason a report cannot be free text.
 *
 * The server keeps a closed set (internal/marketplace/social.go:ReportReasons)
 * because a queue that cannot be grouped cannot be worked. "other" is the
 * escape hatch and is the one code that REQUIRES a note — the server refuses
 * it without one, and saying so here turns a 400 into a hint.
 */
const NOTE_REQUIRED = ["other"];

export function ReportForm({
  subject,
  what,
  onDone,
}: {
  subject: ReportInput["subject"];
  what: string;
  onDone: () => void;
}) {
  const reasons = useReportReasons();
  const report = useReport();
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setLocalError(null);
    if (code === "") {
      setLocalError("Pick a reason.");
      return;
    }
    if (NOTE_REQUIRED.includes(code) && note.trim() === "") {
      setLocalError("“Other” needs a note saying what is wrong.");
      return;
    }
    report.mutate(
      { subject, body: { reason_code: code, note: note.trim() } },
      { onSuccess: onDone },
    );
  };

  return (
    <Card title={`Report this ${what}`}>
      <form onSubmit={submit} noValidate>
        <ErrorAlert error={report.error} title="The report was not filed" />
        {localError ? <Alert tone="danger">{localError}</Alert> : null}
        <Field label="Reason" htmlFor="reason_code">
          <Select id="reason_code" value={code} onChange={(event) => setCode(event.target.value)}>
            <option value="">Pick a reason…</option>
            {(reasons.data ?? []).map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Note"
          htmlFor="reason_note"
          hint={NOTE_REQUIRED.includes(code) ? "Required for this reason." : "Optional."}
        >
          <Input id="reason_note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        <div className="esm-actions">
          <Button type="submit" busy={report.isPending}>
            File report
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
